import * as THREE from 'three';
import { WebGLPathTracer } from 'three-gpu-pathtracer';

/**
 * PhotoMode — toggleable path-traced rendering for photorealistic stills.
 * Uses three-gpu-pathtracer for progressive refinement when the camera stops.
 *
 * Requires WebGLRenderer created with preserveDrawingBuffer: true so the
 * path tracer's accumulated samples survive between animation frames.
 */
export class PhotoMode {
  constructor(renderer, scene, camera) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.enabled = false;
    this.samples = 0;
    this.maxSamples = 400;
    this.pathTracer = null;
    this._initialized = false;
    this._onSampleCallback = null;
    this._onDisableCallback = null;
  }

  onSampleUpdate(cb) {
    this._onSampleCallback = cb;
  }

  onDisable(cb) {
    this._onDisableCallback = cb;
  }

  _init() {
    if (this._initialized) return;

    this.pathTracer = new WebGLPathTracer(this.renderer);
    this.pathTracer.toneMapping = THREE.ACESFilmicToneMapping;
    this.pathTracer.toneMappingExposure = 0.85;
    this.pathTracer.multipleImportanceSampling = true;
    this.pathTracer.bounces = 6;
    this.pathTracer.transmissiveBounces = 3;

    this._initialized = true;
  }

  async enable() {
    try {
      this._init();
      this.samples = 0;

      if (this._onSampleCallback) {
        this._onSampleCallback('Building scene…', 100);
      }

      // Build BVH and set scene — this is the slow part (~500ms for complex scenes).
      // setSceneAsync() requires a BVH worker via setBVHWorker() that we don't wire up,
      // so use the synchronous setScene() (blocks main thread briefly, no worker needed).
      this.pathTracer.setScene(this.scene, this.camera);

      this.enabled = true;
    } catch (err) {
      console.error('PhotoMode enable failed:', err);
      this.enabled = false;
      throw err;
    }
  }

  disable() {
    this.enabled = false;
    this.samples = 0;
    if (this.pathTracer && this._initialized) {
      // Reset internal accumulation buffer
      try { this.pathTracer.reset(); } catch (_) {}
    }
    if (this._onDisableCallback) this._onDisableCallback();
  }

  /** Call when camera moves — resets progressive accumulation */
  cameraChanged() {
    if (!this.enabled || !this.pathTracer) return;
    this.samples = 0;
    try { this.pathTracer.updateCamera(); } catch (_) {}
  }

  /** Called each frame from the animate loop */
  render() {
    if (!this.enabled || !this.pathTracer) return false;

    if (this.samples < this.maxSamples) {
      try {
        this.pathTracer.renderSample();
        this.samples = this.pathTracer.samples ?? (this.samples + 1);
      } catch (err) {
        console.error('PhotoMode render error:', err);
        this.disable();
        return false;
      }

      if (this._onSampleCallback) {
        this._onSampleCallback(this.samples, this.maxSamples);
      }
    }
    return true;
  }
}
